import type { ApiFailure } from './types'

/**
 * REFUSED AT THE DOOR, MID-SESSION (05 6.2).
 *
 * /api/v1/me says at start-up whether this person may come in. This carries the
 * other case, as api/maintenance.ts does for a window: an approval taken back, or
 * a role switched off, while they are already working. The next request is
 * refused at the door with DENIED_ENTRY (or DENIED_PHONE), and App re-reads /me so
 * the whole screen becomes "Access denied" instead of a refusal on each panel.
 *
 * A NOTIFICATION, NOT A DECISION. Every refusal it forwards was already made by the
 * server; suppressing it changes what the screen says and nothing the API answers.
 */

type Listener = () => void

const listeners = new Set<Listener>()

/** Subscribe to door refusals discovered mid-session. Returns the unsubscribe. */
export function onEntryRefused(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/**
 * Called by the API client on every failed request. Only the entry gate's two
 * coarse reasons count: a report's own refusal (DENIED_REPORT) is one panel, not
 * the whole platform. Listener failures are swallowed, as maintenance's are: a
 * throw here would replace an error the screen knows how to show.
 */
export function entryRefusedDetected(failure: ApiFailure): void {
  if (failure.kind !== 'forbidden' || (failure.reason !== 'DENIED_ENTRY' && failure.reason !== 'DENIED_PHONE')) return
  for (const fn of listeners) {
    try {
      fn()
    } catch {
      /* the screen already has the refusal to show */
    }
  }
}
